import React, { useEffect, useState, useCallback } from 'react';
import { Connection, getConnections, connectAccount, removeConnection } from '../api/authService';

const MainPage: React.FC = () => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);

  const loadConnections = useCallback(async () => {
    try {
      const data = await getConnections();
      setConnections(data);
    } catch (error) {
      console.error('Error loading connections:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const handleConnect = () => {
    connectAccount(loadConnections);
  };

  const handleRemove = async (id: number) => {
    try {
      await removeConnection(id);
      setConnections(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error removing connection:', error);
    } finally {
      setConfirmRemoveId(null);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const activeCount = connections.filter(c => c.isActive).length;
  const inactiveCount = connections.filter(c => !c.isActive).length;

  const confirmTarget = confirmRemoveId !== null
    ? connections.find(c => c.id === confirmRemoveId)
    : null;

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-brand">
          <div className="header-logo">QB</div>
          <div>
            <h1 className="header-title">QuickBooks Connections</h1>
            <p className="header-subtitle">Manage your QuickBooks OAuth integrations</p>
          </div>
        </div>
        <button className="btn-add" onClick={handleConnect}>
          + Add Connection
        </button>
      </header>

      <div className="dashboard-content">
        {/* Stats */}
        {!isLoading && connections.length > 0 && (
          <div className="stats-bar">
            <div className="stat-chip">
              <span className="stat-dot green" />
              <div>
                <div className="stat-value">{activeCount}</div>
                <div className="stat-label">Active</div>
              </div>
            </div>
            {inactiveCount > 0 && (
              <div className="stat-chip">
                <span className="stat-dot gray" />
                <div>
                  <div className="stat-value">{inactiveCount}</div>
                  <div className="stat-label">Inactive</div>
                </div>
              </div>
            )}
            <div className="stat-chip">
              <div>
                <div className="stat-value">{connections.length}</div>
                <div className="stat-label">Total</div>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <span>Loading connections...</span>
          </div>
        ) : connections.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <rect width="64" height="64" rx="16" fill="#f0fdf4" />
                <path d="M20 32h24M32 20v24" stroke="#2CA01C" strokeWidth="3" strokeLinecap="round" />
                <rect x="14" y="14" width="36" height="36" rx="8" stroke="#2CA01C" strokeWidth="2" strokeDasharray="4 3" />
              </svg>
            </div>
            <h2 className="empty-title">No Connections Yet</h2>
            <p className="empty-desc">Connect a QuickBooks company to get started. Each company will appear as a card.</p>
            <button className="btn-empty-add" onClick={handleConnect}>
              + Add Your First Connection
            </button>
          </div>
        ) : (
          <div className="connections-grid">
            {connections.map(conn => (
              <div key={conn.id} className="connection-card">
                <div className="card-top">
                  <div className="card-icon">QB</div>
                  <span className={`status-badge ${conn.isActive ? 'active' : 'inactive'}`}>
                    <span className="status-dot" />
                    {conn.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <h3 className="connection-name">
                  {conn.connectionName ?? 'Unknown Company'}
                </h3>

                {conn.companyId && (
                  <p className="connection-company-id">ID: {conn.companyId}</p>
                )}

                <div className="connection-meta">
                  <div className="meta-item">
                    <span className="meta-icon">&#128197;</span>
                    Connected {formatDate(conn.createdAt)}
                  </div>
                  {conn.updatedAt !== conn.createdAt && (
                    <div className="meta-item">
                      <span className="meta-icon">&#128260;</span>
                      Refreshed {formatDate(conn.updatedAt)}
                    </div>
                  )}
                </div>

                <div className="card-actions">
                  <button className="btn-refresh" onClick={handleConnect}>
                    Refresh
                  </button>
                  <button className="btn-remove" onClick={() => setConfirmRemoveId(conn.id)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Remove Modal */}
      {confirmRemoveId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmRemoveId(null)}>
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-icon">&#9888;</div>
            <h3 className="modal-title">Remove Connection</h3>
            <p className="modal-desc">
              Remove <strong>{confirmTarget?.connectionName ?? 'this connection'}</strong>? This deletes
              the stored OAuth tokens and cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setConfirmRemoveId(null)}>
                Cancel
              </button>
              <button className="btn-modal-confirm" onClick={() => handleRemove(confirmRemoveId)}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainPage;
